import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HISTORY_STORAGE_KEY,
  addHistoryEntryToItems,
  normalizeHistoryItems,
  updateHistoryTitleInItems,
} from '../utils/savedData';

export const useHistory = () => {
  const [items, setItems] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  const loadHistory = useCallback(() => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setItems(normalizeHistoryItems(parsed));
        }
      }
    } catch (e) {
      console.warn('Failed to load history from localStorage:', e);
    }
  }, []);

  const add = useCallback((entry) => {
    setItems((prev) => addHistoryEntryToItems(prev, entry));
  }, []);

  const remove = useCallback((id) => {
    setItems(prev => prev.filter(x => x.id !== id));
  }, []);

  const updateTitle = useCallback((id, title) => {
    setItems((prev) => updateHistoryTitleInItems(prev, id, title));
  }, []);

  const clear = useCallback(() => {
    setItems([]);
  }, []);

  const reload = useCallback(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    loadHistory();
    setHydrated(true);
  }, [loadHistory]);

  // Only persist after we've hydrated from storage
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('Failed to save history to localStorage:', e);
    }
  }, [items, hydrated]);

  // Cross-tab sync: when another tab adds / removes / clears history, mirror
  // the change here so both tabs stay consistent. The `storage` event only
  // fires in OTHER tabs, so this won't loop with our own writes.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (e) => {
      if (e.key !== HISTORY_STORAGE_KEY) return;
      try {
        if (e.newValue == null) {
          setItems([]);
          return;
        }
        const parsed = JSON.parse(e.newValue);
        if (Array.isArray(parsed)) {
          setItems(normalizeHistoryItems(parsed));
        }
      } catch (err) {
        console.warn('Failed to sync history across tabs:', err);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return useMemo(() => ({ items, add, remove, updateTitle, clear, reload }), [
    items,
    add,
    remove,
    updateTitle,
    clear,
    reload,
  ]);
};
